import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_ADMIN_REGISTERS_QUERY_KEY } from '@/modules/admin-dashboard/queries/registers.tsx';
import {
  AddNewIssuerRequest,
  AddNewIssuerResponse,
  GetIssuerListResponse,
  GetIssuersV1UsersIssuersGetParams,
} from 'api';

export const CURRENT_ADMIN_ISSUERS_QUERY_KEY = 'vestra/admin-issuers-list';

export const useAdminIssuerListQuery = (
  payload: GetIssuersV1UsersIssuersGetParams,
  disabled = false,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery<GetIssuerListResponse>({
    queryKey: [CURRENT_ADMIN_ISSUERS_QUERY_KEY, JSON.stringify(data)],
    staleTime: 1000 * 60 * 60, // 1 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getIssuersV1UsersIssuersGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    refetchInterval: 1000 * 60 * 5, //5min
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useAdminInviteIssuerMutation = ({
  setErrors,
}: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: AddNewIssuerRequest) => {
      const payloadWithRedirectUrl = {
        ...payload,
        redirect_url: import.meta.env.VITE_SR_URL,
      };
      const response = await withErrorHandling<AddNewIssuerResponse>(
        api.v1.addNewIssuerV1UsersIssuersPost(payloadWithRedirectUrl),
        {
          loading: 'Sending invite...',
          success: 'Invite sent',
          setErrors,
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_ADMIN_REGISTERS_QUERY_KEY]);
    },
  });
};

export const useAdminDeleteUserRegisterMutation = ({
  onSuccess,
}: { onSuccess?: () => void } = {}) => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.deleteUserRegistersV1SrUserRegistersIdDelete({ id }),
        {
          loading: 'Removing register from issuer...',
          success: 'Register removed',
        },
      );

      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([CURRENT_ADMIN_ISSUERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ADMIN_REGISTERS_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};
